import { all, takeEvery, put } from 'redux-saga/effects'
import localforage from 'myStore'
import { store as reduxStore } from 'index'
import { isPopupMode } from 'services/popup/isPopupMode'
import actions from './actions'
import { ChangeSettingPayload } from './types'

// SCSS $breakpoint-str 값과 동일. (scss/abstracts/_variables.scss의 :export 블록 참조)
const breakpointStr = 'mobile|600|tablet|1280|desktop'

interface BreakpointRange {
  min: number
  max: number
}

interface BreakpointMap {
  [name: string]: BreakpointRange
}

interface SettingsInfo {
  ranges: BreakpointMap[]
  resizeCallback?: () => void
  resizeTimer?: ReturnType<typeof setTimeout> | null
  resizeCalled?: boolean
}

const parsedBreakpoints = (str: string): BreakpointMap[] => {
  const ranges: BreakpointMap[] = []
  const groups = str.split('||')
  groups.forEach((groupStr: string) => {
    const group = groupStr.split('|')
    const data: (string | number)[] = []
    group.forEach((item: string, i: number) => {
      if (i % 2 === 0) {
        data.push(item)
      } else {
        const num = parseInt(item, 10)
        data.push(num)
      }
    })
    const map: BreakpointMap = {}
    for (let i = 0; i < data.length; i += 2) {
      const range: BreakpointRange = {
        min: i > 0 ? (data[i - 1] as number) + 1 : 0,
        max: i < data.length - 1 ? (data[i + 1] as number) : Number.MAX_SAFE_INTEGER,
      }
      map[data[i] as string] = range
    }
    ranges.push(map)
  })
  return ranges
}

const settingsInfo: SettingsInfo = {
  ranges: parsedBreakpoints(breakpointStr),
}

export function* CHANGE_SETTING(action: { type: string; payload: ChangeSettingPayload }) {
  const {
    payload: { setting, value },
  } = action
  const saveSetting = async (): Promise<void> => {
    // popup(EmailEditorWindow)은 부모와 공유하는 localforage 의 viewMode/isMobileView 를
    // 자신의 창 크기로 덮어쓰지 않도록 persist 를 스킵한다. SET_STATE(아래)는 유지.
    if (isPopupMode()) return
    await localforage.setItem(`app.appsettings.${setting}`, value)
  }

  yield saveSetting()

  yield put({
    type: 'appsettings/SET_STATE',
    payload: {
      [setting]: value,
    },
  })
}

export function* SETUP() {
  const checkViewMode = async (load = false): Promise<void> => {
    const curWidth = (global as typeof globalThis & { window: Window }).window.innerWidth
    const { ranges } = settingsInfo
    let currentState = null
    const defaultRanges = ranges[0]
    for (const key in defaultRanges) {
      const { min, max } = defaultRanges[key]
      if (curWidth >= min && curWidth <= max) {
        currentState = key
        break
      }
    }
    const prevState = await localforage.getItem('app.appsettings.viewMode')
    if (currentState !== prevState || load) {
      reduxStore.dispatch({
        type: 'appsettings/CHANGE_SETTING',
        payload: {
          setting: 'viewMode',
          value: currentState,
        },
      })

      const isMobileView = currentState === 'mobile'
      reduxStore.dispatch({
        type: 'appsettings/CHANGE_SETTING',
        payload: {
          setting: 'isMobileView',
          value: isMobileView,
        },
      })
    }
  }

  yield checkViewMode(true)

  if (window.parent === window) {
    if (settingsInfo.resizeCallback != null) {
      yield window.removeEventListener('resize', settingsInfo.resizeCallback)
    }

    settingsInfo.resizeCallback = () => {
      if (settingsInfo.resizeTimer != null) {
        settingsInfo.resizeCalled = true
        return
      }
      checkViewMode()
      settingsInfo.resizeCalled = false
      settingsInfo.resizeTimer = setTimeout(() => {
        settingsInfo.resizeTimer = null
        if (settingsInfo.resizeCalled === true) {
          settingsInfo.resizeCallback()
        }
      }, 300)
    }

    yield window.addEventListener('resize', settingsInfo.resizeCallback)
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actions.CHANGE_SETTING, CHANGE_SETTING), SETUP()])
}
